import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { CookieConsent } from './components/CookieConsent';
import { ImagePreloader } from './components/ImagePreloader';
import { ScrollToTop } from './components/ScrollToTop';
import { Loader2 } from 'lucide-react';

const CardCreator = lazy(() => import('./pages/CardCreator'));

const CRITICAL_IMAGES = [
  'https://raw.githubusercontent.com/NiroWeb/logo/refs/heads/main/efootball-2025-logo-white-260x300.webp',
];

const LoadingFallback = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex items-center gap-3">
      <Loader2 className="w-6 h-6 animate-spin" />
      <div className="text-xl">Loading...</div>
    </div>
  </div>
);

const SocialBar = () => (
  <header className="w-full bg-neutral-900/80 backdrop-blur border-b border-neutral-800">
    <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-end gap-2 sm:gap-3">
      <a
        href="https://x.com/efootball_run"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow eFootball.run on X"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-neutral-800 text-neutral-200 hover:bg-yellow-500 hover:text-black transition-colors"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        Follow on X
      </a>
      <a
        href="https://discord.com/invite/U7nFxKVvJb"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Join the eFootball.run Discord server"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-neutral-800 text-neutral-200 hover:bg-yellow-500 hover:text-black transition-colors"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
        Join Discord
      </a>
    </div>
  </header>
);

function ensureCookiePortal() {
  if (!document.getElementById('cookie-consent-portal')) {
    const portalEl = document.createElement('div');
    portalEl.id = 'cookie-consent-portal';
    document.body.appendChild(portalEl);
    return true;
  }
  return true;
}

function App() {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [cookiePortalReady, setCookiePortalReady] = useState(false);

  useEffect(() => {
    setCookiePortalReady(ensureCookiePortal());
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
    });
  }, []);

  return (
    <>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-black text-white">
          <ImagePreloader imageUrls={CRITICAL_IMAGES} format="auto" />
          <SocialBar />
          <Routes>
            <Route
              path="/card-creator"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CardCreator />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/card-creator" replace />} />
          </Routes>
        </div>
      </Router>

      {!isInitialLoad && cookiePortalReady
        ? createPortal(
            <CookieConsent />,
            document.getElementById('cookie-consent-portal')!
          )
        : !isInitialLoad && <CookieConsent />}
    </>
  );
}

export default App;
